import { Head, router, useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface User {
  id: number
  name: string
}

interface AssignmentData {
  id: number
  equipment_name: string | null
  equipment_type: string | null
  assigned_to: string | null
  assigned_at: string | null
  returned_at: string | null
  removed_by_id: number | null
  start_odometer: number | null
  end_odometer: number | null
  billable_amount: number | null
  notes: string
  install_vehicle: string | null
  install_vehicle_location: string | null
  install_gps_location: string | null
  remove_vehicle_location: string
  requires_vehicle: boolean
  requires_odometer: boolean
  requires_location: boolean
}

interface Props {
  case_record: CaseRecord
  assignment: AssignmentData
  users: User[]
  errors?: Record<string, string[]>
}

export default function Edit({
  case_record,
  assignment,
  users,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    {
      title: "Equipment",
      href: `/cases/${case_record.id}/equipment_assignments`,
    },
    {
      title: "Return Details",
      href: `/cases/${case_record.id}/equipment_assignments/${assignment.id}/edit`,
    },
  ]

  const { data, setData, patch, processing, transform } = useForm({
    returned_at: assignment.returned_at ?? "",
    removed_by_id: assignment.removed_by_id,
    end_odometer: assignment.end_odometer,
    remove_vehicle_location: assignment.remove_vehicle_location || "",
    notes: assignment.notes || "",
    billable_amount: assignment.billable_amount,
  })

  transform((data) => ({ equipment_assignment: data }))

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    patch(`/cases/${case_record.id}/equipment_assignments/${assignment.id}`)
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Return Equipment - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Record Equipment Return</h1>

        <div className="rounded-lg bg-white p-6 shadow">
          {/* Read-only assignment info */}
          <div className="border-hpi-light-gray mb-6 space-y-1 border-b pb-4">
            <h3 className="text-lg font-medium">Assignment Details</h3>
            <div className="text-hpi-dark-gray text-sm">
              <div>
                <strong>Equipment:</strong> {assignment.equipment_name ?? "—"}
              </div>
              <div>
                <strong>Assigned To:</strong> {assignment.assigned_to ?? "—"} on{" "}
                {assignment.assigned_at ?? "—"}
              </div>
              {assignment.install_vehicle && (
                <div>
                  <strong>Vehicle:</strong> {assignment.install_vehicle}
                </div>
              )}
              {assignment.install_gps_location && (
                <div>
                  <strong>Device Placement:</strong>{" "}
                  {assignment.install_gps_location}
                </div>
              )}
              {assignment.install_vehicle_location && (
                <div>
                  <strong>Vehicle Location at Install:</strong>{" "}
                  {assignment.install_vehicle_location}
                </div>
              )}
              {assignment.start_odometer != null &&
                assignment.start_odometer > 0 && (
                  <div>
                    <strong>Start Odo/Hours:</strong>{" "}
                    {assignment.start_odometer.toLocaleString("en-US", {
                      maximumFractionDigits: 0,
                    })}
                  </div>
                )}
            </div>
          </div>

          {/* Return form */}
          <form onSubmit={submit} className="space-y-6">
            <h3 className="text-lg font-medium">Return Details</h3>

            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label className={labelClass}>Date Returned</label>
                <input
                  type="datetime-local"
                  className={inputClass}
                  value={data.returned_at}
                  onChange={(e) => setData("returned_at", e.target.value)}
                  required
                />
                {errors?.returned_at && (
                  <p className={errorClass}>{errors.returned_at[0]}</p>
                )}
              </div>

              <div>
                <label className={labelClass}>Removed By</label>
                <select
                  className={inputClass}
                  value={data.removed_by_id ?? ""}
                  onChange={(e) =>
                    setData(
                      "removed_by_id",
                      e.target.value ? parseInt(e.target.value) : null,
                    )
                  }
                  required
                >
                  <option value="">Select employee...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
                {errors?.removed_by && (
                  <p className={errorClass}>{errors.removed_by[0]}</p>
                )}
                {errors?.removed_by_id && (
                  <p className={errorClass}>{errors.removed_by_id[0]}</p>
                )}
              </div>
            </div>

            {/* Conditional: Vehicle location when removed (GPS equipment) */}
            {assignment.requires_vehicle && (
              <div>
                <label className={labelClass}>
                  Location of Vehicle When Device Removed
                </label>
                <textarea
                  className={inputClass}
                  rows={3}
                  value={data.remove_vehicle_location}
                  onChange={(e) =>
                    setData("remove_vehicle_location", e.target.value)
                  }
                  placeholder="Address or description of vehicle location"
                  required
                />
                {errors?.remove_vehicle_location && (
                  <p className={errorClass}>
                    {errors.remove_vehicle_location[0]}
                  </p>
                )}
              </div>
            )}

            {/* Conditional: End odometer */}
            {assignment.requires_odometer && (
              <div>
                <label className={labelClass}>End Odometer / Hours</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={inputClass}
                  value={data.end_odometer ?? ""}
                  onChange={(e) =>
                    setData(
                      "end_odometer",
                      e.target.value ? parseFloat(e.target.value) : null,
                    )
                  }
                  required
                />
                {errors?.end_odometer && (
                  <p className={errorClass}>{errors.end_odometer[0]}</p>
                )}
              </div>
            )}

            <div>
              <label className={labelClass}>Billable Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputClass}
                value={data.billable_amount ?? ""}
                onChange={(e) =>
                  setData(
                    "billable_amount",
                    e.target.value ? parseFloat(e.target.value) : null,
                  )
                }
              />
              {errors?.billable_amount && (
                <p className={errorClass}>{errors.billable_amount[0]}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>Notes</label>
              <textarea
                className={inputClass}
                rows={3}
                value={data.notes}
                onChange={(e) => setData("notes", e.target.value)}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() =>
                  router.get(`/cases/${case_record.id}/equipment_assignments`)
                }
                className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {processing ? "Saving..." : "Save Return Details"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
